/**
 * Playground stand-in for GameConfigRebaseEngine — what happens to a branch when its parent's
 * content changes.
 *
 * The delta/merge and the classification are the real rules; the reasons are shorter, and BLOCKED is
 * detected for objects only (no keyed lists). Good enough to drive the review dialog; the server's answer
 * is the one that decides what is written.
 */

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export const parseJson = (text) => {
    try {
        return JSON.parse(text || '{}');
    } catch {
        return {};
    }
};

const deltaOf = (base, next) => {
    if (!isObj(base) || !isObj(next)) return next;
    const out = {};
    Object.keys(next).forEach((key) => {
        if (!(key in base)) out[key] = next[key];
        else if (isObj(base[key]) && isObj(next[key])) {
            const inner = deltaOf(base[key], next[key]);
            if (isObj(inner) && Object.keys(inner).length) out[key] = inner;
        } else if (JSON.stringify(base[key]) !== JSON.stringify(next[key])) out[key] = next[key];
    });
    return out;
};

const mergeInto = (base, delta) => {
    if (!isObj(base) || !isObj(delta)) return delta;
    const out = { ...base };
    Object.keys(delta).forEach((key) => {
        out[key] = isObj(out[key]) && isObj(delta[key]) ? mergeInto(out[key], delta[key]) : delta[key];
    });
    return out;
};

const leafPaths = (tree, prefix = '', out = []) => {
    if (!isObj(tree)) return out;
    Object.keys(tree).forEach((key) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (isObj(tree[key])) leafPaths(tree[key], path, out);
        else out.push(path);
    });
    return out;
};

const WEIGHT_FIELDS = ['pct', 'percent', 'weight', 'chance', 'probability'];

// Every block whose entries all carry the same numeric weight field, with its total.
const tableTotals = (tree, prefix = '', out = []) => {
    if (!isObj(tree)) return out;
    const entries = Object.values(tree);
    const field = WEIGHT_FIELDS.find((f) => entries.length
        && entries.every((e) => isObj(e) && typeof e[f] === 'number'));
    if (field) out.push({ blockPath: prefix, field, total: entries.reduce((sum, e) => sum + e[field], 0) });
    Object.keys(tree).forEach((key) => tableTotals(tree[key], prefix ? `${prefix}.${key}` : key, out));
    return out;
};

const shiftedTotals = (before, after) => tableTotals(before)
    .map((was) => {
        const is = tableTotals(after).find((a) => a.blockPath === was.blockPath && a.field === was.field);
        return is && is.total !== was.total
            ? { blockPath: was.blockPath, field: was.field, before: was.total, after: is.total }
            : null;
    })
    .filter(Boolean);

// Every object path whose DIRECT key set differs — a fifth reward appearing beside four the branch
// rebalanced is a change at `rewards`, not at the root.
const keySetChanges = (before, after, path = '', out = []) => {
    if (!isObj(before) || !isObj(after)) return out;
    const added = Object.keys(after).filter((k) => !(k in before));
    const removed = Object.keys(before).filter((k) => !(k in after));
    if (added.length || removed.length) out.push({ path, added, removed });
    Object.keys(before).forEach((key) => {
        if (key in after) keySetChanges(before[key], after[key], path ? `${path}.${key}` : key, out);
    });
    return out;
};

// Whether the branch overrides anything inside this object path ('' being the root).
const ownsInside = (owned, path) => (path
    ? owned.some((p) => p.startsWith(`${path}.`))
    : owned.length > 0);

const valueAt = (tree, path) => path.split('.').reduce((node, key) => (isObj(node) ? node[key] : undefined), tree);

// Why this branch's own values can't be placed on the new parent: a container they sit in turned into something
// else, or the parent put an object exactly where the branch sets a value of its own (the server's BLOCKED).
const blockedReasons = (parentName, owned, before, after) => {
    const reasons = new Set();
    owned.forEach((path) => {
        const keys = path.split('.');
        for (let i = 1; i < keys.length; i += 1) {
            const container = keys.slice(0, i).join('.');
            const node = valueAt(after, container);
            if (node === undefined) return;
            if (!isObj(node)) {
                reasons.add(`${parentName} turned ${container} into a different kind of value, and this branch sets its own values inside it.`);
                return;
            }
        }
        if (!isObj(valueAt(after, path))) return;
        const was = valueAt(before, path);
        const what = was === undefined ? `${parentName} added ${path} as an object`
            : isObj(was) ? `${parentName} has an object at ${path}` : `${parentName} turned ${path} into an object`;
        reasons.add(`${what}, and this branch sets its own value there.`);
    });
    return [...reasons];
};

export const rebase = (parentName, parentOld, parentNew, childContent) => {
    const before = parseJson(parentOld);
    const after = parseJson(parentNew);
    const current = parseJson(childContent);

    const delta = deltaOf(before, current);
    const owned = leafPaths(delta);
    const blocking = blockedReasons(parentName, owned, before, after);
    if (blocking.length) {
        return {
            classification: 'BLOCKED', reasons: blocking, rebasedContent: null, tableTotals: [],
        };
    }
    const rebasedTree = mergeInto(after, delta);

    const flatBefore = leafPaths(before);
    const flatAfter = leafPaths(after);
    const touched = owned.filter((p) => flatBefore.includes(p) && flatAfter.includes(p));
    const totals = shiftedTotals(current, rebasedTree);
    const structural = keySetChanges(before, after)
        .filter((change) => ownsInside(owned, change.path));

    let classification = 'FOLLOWS';
    let reasons = [`Nothing this branch sets is near the change — it follows ${parentName}.`];

    if (structural.length) {
        classification = 'NEEDS_REVIEW';
        reasons = [];
        structural.forEach(({ path, added, removed }) => {
            const where = path || 'the top level';
            if (added.length) {
                reasons.push(`${parentName} added ${added.join(', ')} to ${where}; this branch sets its own values there.`);
            }
            if (removed.length) {
                reasons.push(`${parentName} removed ${removed.join(', ')} from ${where}; this branch still sets its own values there.`);
            }
        });
    } else if (touched.length) {
        classification = 'PARTLY_FOLLOWS';
        reasons = touched.map((p) => `${parentName} changed ${p}, but this branch overrides it — the branch keeps its own value.`);
    } else if (totals.length) {
        classification = 'PARTLY_FOLLOWS';
        reasons = [`${parentName} changed values beside this branch's own — it follows them and keeps its own.`];
    }

    return {
        classification,
        reasons,
        rebasedContent: `${JSON.stringify(rebasedTree, null, 2)}\n`,
        tableTotals: totals,
    };
};
